/**
 * Stripe Checkout success-redirect target.
 *
 * Stripe sends the user back here as `?session_id=cs_test_...` and we then
 * poll `/api/payments/checkout/status/{session_id}` until the row is
 * `paid`, `failed`, or `expired`. Polling pattern:
 *   first 5 attempts at 2s
 *   next 5 attempts at 4s
 * Hard cap at ~30s to stop infinite loops if the webhook is delayed.
 *
 * Why poll: the playbook strongly recommends not trusting redirect alone
 * because the webhook may not have fired by the time the user lands here,
 * and `success_url` itself isn't a payment guarantee.
 */
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { api, errorMessage } from "../../lib/api";

const POLLING_PLAN_MS = [2000, 2000, 2000, 2000, 2000, 4000, 4000, 4000, 4000, 4000];

export default function PaymentSuccess() {
    const { sid } = useParams();
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");

    const [phase, setPhase] = useState("polling"); // polling | paid | failed | expired | error
    const [info, setInfo] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!sessionId) {
            setPhase("error");
            setErr("Missing session_id in URL.");
            return undefined;
        }
        let cancelled = false;
        let attempt = 0;
        let timer;

        const poll = async () => {
            if (cancelled) return;
            try {
                const r = await api.get(`/payments/checkout/status/${sessionId}`);
                if (cancelled) return;
                setInfo(r.data);
                const status = r.data?.status;
                if (status === "paid") { setPhase("paid"); return; }
                if (status === "failed") { setPhase("failed"); return; }
                if (status === "expired") { setPhase("expired"); return; }
            } catch (e) {
                setErr(errorMessage(e));
            }
            if (cancelled) return;
            if (attempt >= POLLING_PLAN_MS.length) {
                setPhase("error");
                setErr("We're still confirming your payment. If you weren't charged, no need to worry. Otherwise, please refresh in a minute.");
                return;
            }
            timer = setTimeout(poll, POLLING_PLAN_MS[attempt]);
            attempt += 1;
        };

        poll();
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [sessionId]);

    const survey = info?.survey_id || sid;

    return (
        <div className="bg-paper min-h-screen">
            <Navbar />
            <section className="max-w-xl mx-auto px-6 py-20 text-center" data-testid="payment-success-page">
                {phase === "polling" && (
                    <div data-testid="payment-state-polling">
                        <p className="eyebrow mb-3">PROCESSING</p>
                        <h1 className="h1 mb-3">Confirming your payment…</h1>
                        <p className="text-ink-600">This usually takes a few seconds.</p>
                        <div className="mt-8 inline-block w-3 h-3 bg-ink animate-pulse" />
                    </div>
                )}
                {phase === "paid" && (
                    <div data-testid="payment-state-paid">
                        <p className="eyebrow mb-3">PAYMENT CONFIRMED</p>
                        <h1 className="h1 mb-3">Thank you.</h1>
                        <p className="text-ink-600 mb-2">
                            We charged your card <strong>${(info?.amount_usd || 0).toFixed(2)}</strong> in Stripe test mode.
                        </p>
                        {info?.kind === "survey_funding" && (
                            <p className="text-ink-600 mb-8">Your survey has been queued for admin review.</p>
                        )}
                        {info?.kind === "extension" && (
                            <p className="text-ink-600 mb-8">Your survey has been topped up. New invitations are flowing now.</p>
                        )}
                        <Link to={`/advertiser/surveys/${survey}`} className="btn btn-primary" data-testid="payment-success-cta">
                            Back to survey
                        </Link>
                    </div>
                )}
                {phase === "failed" && (
                    <div data-testid="payment-state-failed">
                        <p className="eyebrow mb-3 text-error">PAYMENT FAILED</p>
                        <h1 className="h1 mb-3">Something went wrong.</h1>
                        <p className="text-ink-600 mb-8">Your card was not charged. Please try again or contact support.</p>
                        <Link to={`/advertiser/surveys/${survey}/edit`} className="btn btn-primary">Try again</Link>
                    </div>
                )}
                {phase === "expired" && (
                    <div data-testid="payment-state-expired">
                        <p className="eyebrow mb-3">SESSION EXPIRED</p>
                        <h1 className="h1 mb-3">This checkout session expired.</h1>
                        <p className="text-ink-600 mb-8">No charge was made. Please start the payment again.</p>
                        <Link to={`/advertiser/surveys/${survey}/edit`} className="btn btn-primary">Try again</Link>
                    </div>
                )}
                {phase === "error" && (
                    <div data-testid="payment-state-error">
                        <p className="eyebrow mb-3 text-error">UH OH</p>
                        <h1 className="h1 mb-3">We couldn't confirm right away.</h1>
                        <p className="text-ink-600 mb-8">{err}</p>
                        <Link to="/advertiser" className="btn btn-primary">Back to dashboard</Link>
                    </div>
                )}
            </section>
            <Footer />
        </div>
    );
}
